import { toast } from 'react-toastify';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import { ButtonType } from '../../types/button-type';
import { generatePath } from 'react-router-dom';
import ROUTE from '../../constants/route-constants';
import { connect } from 'react-redux';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';

// Store
import { Dispatch, RootState } from '../../store/store';
import { CycleComponents, DataZoomEvent } from '../CycleChartPreview/types';
import { getLttbData } from '../CycleChartPreview/helpers';
import { CycleReportItem, ITakeGraphScreenshotProps, ITakeMainScreenshotProps } from '../../store/models/reports';
import { useTranslation } from 'react-i18next'
import { CycleController } from '../../store/models/controllers';
import { CHARACTER_SEPARATOR_DEVICE_NAME, IOT_DEVICE_TYPE, SET_POINT_NAME } from '../CycleChartPreview/constants';
import { CycleReports } from '../../store/models/cycles';

import { appConfig } from '../../configs/app-config';

interface IGraphScreenshotPropsV2 {
  chartRef: React.MutableRefObject<ReactECharts | null>;
  clientId: string;
  contractId: string;
  cycleComponents: CycleComponents;
  cycleControllers: CycleController[];
  cycleId: string;
  cycleReportItems: CycleReportItem[];
  cycleReports: CycleReports[];
  dataZoom: DataZoomEvent;
  loading: boolean;
  mainScreenLoading: boolean;
  screenId: string | undefined;
  takeGraphScreenshot: (data: ITakeGraphScreenshotProps) => Promise<void>;
  takeMainGraphScreenshot: (data: ITakeMainScreenshotProps) => Promise<void>;
}

const GraphScreenshotV2 = (data: IGraphScreenshotPropsV2) => {
  const {
    loading,
    mainScreenLoading,
    cycleId,
    dataZoom,
    chartRef,
    cycleComponents,
    cycleReportItems,
    cycleControllers,
    takeGraphScreenshot,
    takeMainGraphScreenshot,
    clientId,
    contractId,
    screenId,
    cycleReports,
  } = data;

  const isShowCycleReportButton = appConfig.apiVersion === '2';

  const channelsToHide = cycleReportItems.flatMap((device) => {
    const controllerName = cycleControllers.find((item) => item.controller.id === device.controllerId)?.controller.name || null;
    if (!controllerName) return [];

    if (!device.excluded) {
      return [];
    }

    const iotDeviceName = `${IOT_DEVICE_TYPE[device.type]}${CHARACTER_SEPARATOR_DEVICE_NAME}${device.itemNumber}`
    return `${controllerName}#${iotDeviceName}`;
  });



  const pathToCycleReport = screenId
    ? generatePath(ROUTE.CYCLE_REPORT, { id: cycleId, clientId, contractId, screenId })
    : generatePath(ROUTE.CYCLE_REPORT_WITHOUT_SCREEN, { id: cycleId, clientId, contractId });

  const cycleReportId = cycleReports?.find((report) => report.isActive).id

  const pathToCycleReportPage = screenId
    ? generatePath(ROUTE.CYCLE_REPORTS, { id: cycleId, cycleReportId, clientId, contractId, screenId })
    : generatePath(ROUTE.CYCLE_REPORTS_WITHOUT_SCREEN, { id: cycleId, cycleReportId, clientId, contractId })

  const takeScreenshot = async (options: any, coordRange?: (string | number | Date)[][]) => {
    const filteredDataZoom = options.dataZoom.filter(item => item.type !== 'slider');
    const filteredOptions = { ...options, dataZoom: filteredDataZoom };

    filteredOptions.series = filteredOptions.series.filter((item) => !channelsToHide.includes((item.name)) && item.id !== SET_POINT_NAME)

    const chartContainer = document.createElement('div');
    chartContainer.style.width = '1920px';
    chartContainer.style.height = '1080px';

    const tempChart = echarts.init(chartContainer);

    tempChart.setOption(filteredOptions);

    return new Promise<Blob>((resolve) => {
      if (coordRange) {
        tempChart.dispatchAction({
          type: 'brush',
          areas: [
            {
              brushType: 'rect',
              xAxisIndex: 0,
              yAxisIndex: 0,
              coordRange,
            }
          ]
        });
      }
      tempChart.resize();
      tempChart.on('finished', () => {
        const base64 = tempChart.getDataURL({ type: 'png' });
        const byteCharacters = atob(base64.split(',')[1]);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        resolve(new Blob([byteArray], { type: 'image/png' }));
      });
    });
  };

  const handleScreenshot = async () => {
    try {
      const eChartInstance = chartRef.current?.getEchartsInstance();
      if (!eChartInstance) return;

      const channels = cycleComponents.deviceSeriesData.map((device) => device.name.split('#').at(1))
      const originalOptions = eChartInstance.getOption();

      const xAxisArray = Array.isArray(originalOptions.xAxis) ? originalOptions.xAxis : [originalOptions.xAxis]
      originalOptions.xAxis = xAxisArray.map(axis => ({
        ...axis,
        axisLabel: {
          ...axis.axisLabel,
          fontSize: 20,
        },
      }));

      const yAxisArray = Array.isArray(originalOptions.yAxis) ? originalOptions.yAxis : [originalOptions.yAxis]
      originalOptions.yAxis = yAxisArray.map(yAxis => ({
        ...yAxis,
        axisLabel: {
          ...yAxis.axisLabel,
          fontSize: 20
        },
      }));

      const [yData, xData] = originalOptions.dataZoom ?? [{ start: 0, end: 100 }, { start: 0, end: 100 }];

      const zoomedScreenshot = await takeScreenshot(originalOptions);
      let previewScreenshot = undefined;

      if (yData.start !== 0 || yData.end !== 100 || xData.start !== 0 || xData.end !== 100) {
        const coordRange = [
          [xData.startValue, xData.endValue],
          [yData.startValue, yData.endValue]
        ];

        const newVisibleData = getLttbData(cycleComponents.source.length, cycleComponents.source);

        const optionsWithoutZoom = {
          ...originalOptions,
          dataZoom: [
            {
              ...originalOptions.dataZoom.at(0),
              end: 100,
              start: 0
            },
            {
              ...originalOptions.dataZoom.at(1),
              end: 100,
              start: 0
            },
          ],
          series: originalOptions.series.map((seriesItem) => ({
            ...seriesItem,
            data: newVisibleData.map((d) => [d.time, d[seriesItem.id]]),
          })),
          brush: {
            toolbox: ['rect', 'clear'],
            brushType: 'rect',
            brushMode: 'single',
            xAxisIndex: 'all',
            transformable: true,
            brushStyle: {
              borderWidth: 4,
              color: 'transparent',
              borderColor: 'red',
            }
          },
        };

        previewScreenshot = await takeScreenshot(optionsWithoutZoom, coordRange);
      }

      await takeGraphScreenshot({
        cycleId, dataZoom,
        screenshot: zoomedScreenshot,
        previewScreenshot,
        timeLine: { from: Number(xData.startValue), to: Number(xData.endValue) },
        channels,
      });

      toast.success('Graph screenshot request sent successfully');
    } catch (error) {
      toast.error('Failed to send screenshot request');
      console.error(error);
    }
  };

  const handleMainScreenshot = async () => {
    const eChartInstance = chartRef.current?.getEchartsInstance();
    if (!eChartInstance) return;

    const originalOptions = eChartInstance.getOption();

    const xAxisArray = Array.isArray(originalOptions.xAxis) ? originalOptions.xAxis : [originalOptions.xAxis]
    originalOptions.xAxis = xAxisArray.map(axis => ({
      ...axis,
      axisLabel: {
        ...axis.axisLabel,
        fontSize: 20,
      },
    }));

    const yAxisArray = Array.isArray(originalOptions.yAxis) ? originalOptions.yAxis : [originalOptions.yAxis]
    originalOptions.yAxis = yAxisArray.map(yAxis => ({
      ...yAxis,
      axisLabel: {
        ...yAxis.axisLabel,
        fontSize: 20
      },
    }));

    const newVisibleData = getLttbData(cycleComponents.source.length, cycleComponents.source);

    const optionsWithoutZoom = {
      ...originalOptions,
      dataZoom: [
        {
          ...originalOptions.dataZoom.at(0),
          end: 100,
          start: 0
        },
        {
          ...originalOptions.dataZoom.at(1),
          end: 100,
          start: 0
        },
      ],
      series: originalOptions.series.map((seriesItem) => ({
        ...seriesItem,
        data: newVisibleData.map((d) => [d.time, d[seriesItem.id]]),
      })),
    };

    const unZoomedScreenshot = await takeScreenshot(optionsWithoutZoom);
    await takeMainGraphScreenshot({
      cycleId,
      screenshot: unZoomedScreenshot,
    });
  }

  const { t } = useTranslation();

  const buttonClassnames = 'rounded-md bg-primary px-4 py-1.5 font-bold text-white hover:bg-[#ff9146]'
  const cycleComponentsExists = cycleComponents && cycleComponents.source.length > 0;

  return (
    <div className="c-graph-screenshot c-graph-screenshot_v2 flex items-center gap-3">
      {cycleComponentsExists && (
        <>
          <InteractiveButton
            className={buttonClassnames}
            type={ButtonType.Primary}
            text={mainScreenLoading ? t('labels.processing') : t('labels.takeMainChartSnapshot')}
            onClick={handleMainScreenshot}
            disabled={mainScreenLoading}
            as="button"
          />
          <InteractiveButton
            className={buttonClassnames}
            type={ButtonType.Primary}
            text={loading ? t('labels.processing') : t('labels.takeZoomedInSnapshot')}
            onClick={handleScreenshot}
            disabled={loading}
            as="button"
          />
        </>
      )}
      {!isShowCycleReportButton && (
        <InteractiveButton
          className={buttonClassnames}
          type={ButtonType.Primary}
          iconLeft={null}
          text={t('labels.openReportPage')}
          onClick={() => { }}
          as="a"
          href={pathToCycleReport}
          target="_blank"
        />
      )}
      {isShowCycleReportButton && (
        <InteractiveButton
          className={buttonClassnames}
          type={ButtonType.Primary}
          iconLeft={null}
          text={t('labels.openCycleReportPage')}
          onClick={() => { }}
          as="a"
          href={pathToCycleReportPage}
          target="_blank"
        />
      )}
    </div>
  );
};

const mapState = (state: RootState) => ({
  loading: state.reports.loading,
  mainScreenLoading: state.reports.mainScreenLoading,
});

const mapDispatch = (dispatch: Dispatch) => ({
  takeGraphScreenshot: dispatch.reports.takeGraphScreenshot,
  takeMainGraphScreenshot: dispatch.reports.takeMainGraphScreenshot,
});

export default connect(mapState, mapDispatch)(GraphScreenshotV2);
